import { useSyncExternalStore } from "react";
import {
  BracketStore,
  OFFSET_UNITS,
  contractFactsFor,
  type BracketDoc,
  type ContractFacts,
  type KeyValueStorage,
  type OffsetUnit,
} from "@helm/core";
import type { InstrumentFacts } from "../md/marketData";

/**
 * The window's bracket strategies (`BracketStore` in `@helm/core`: pure rules, localStorage cache,
 * synced per user as `bracket_presets/main`). The dev preview switches to its own local-only copy
 * before anything renders (`localOnlyBracketStore`).
 */

const SYNCED_KEY = "helm:brackets";
const PREVIEW_KEY = "helm:brackets:preview";

const localStore: KeyValueStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota / private mode: presets simply aren't cached locally.
    }
  },
};

let preview = false;
let store: BracketStore | null = null;

/** Dev preview: keep presets in local storage only, never the API. Call before first render. */
export function localOnlyBracketStore(): void {
  preview = true;
}

export function bracketStore(): BracketStore {
  store ??= new BracketStore({
    storageKey: preview ? PREVIEW_KEY : SYNCED_KEY,
    storage: localStore,
    sync: !preview,
  });
  return store;
}

/** Route a bracket_presets `DocChanged` (session.ts) to the store. */
export function bracketDocChanged(docId: string, version: number): void {
  if (docId !== "main" || store === null) return;
  void store.refresh(version);
}

export function useBracketDoc(): BracketDoc {
  const s = bracketStore();
  return useSyncExternalStore(s.subscribe, s.getSnapshot);
}

export function newPresetId(): string {
  return `br-${crypto.randomUUID().slice(0, 8)}`;
}

/**
 * Tick size / tick value for `symbol`: the live `InstrumentInfo` wins (`live: true`), else the CME
 * table. Only live facts size a points / $ template (ADR-0049); the table still sizes the older
 * $ legs (Q-B1) as before, and previews.
 */
export function contractFacts(
  symbol: string | null,
  info: InstrumentFacts | null | undefined,
): ContractFacts | null {
  if (info && info.tickSizeDen > 0 && info.tickSizeNum > 0 && info.tickValueCents > 0) {
    return {
      tickSizeNum: info.tickSizeNum,
      tickSizeDen: info.tickSizeDen,
      tickValueCents: info.tickValueCents,
      live: true,
    };
  }
  return contractFactsFor(symbol);
}

// ---- the bracket control's display unit (ticks / pts / $; ADR-0049) -----------------------

const UNIT_KEY = "helm:brackets:unit";
let displayUnit: OffsetUnit | null = null;
let displayUnitRead = false;
const unitListeners = new Set<() => void>();

const readDisplayUnit = (): OffsetUnit | null => {
  if (!displayUnitRead) {
    displayUnitRead = true;
    const v = localStore.get(UNIT_KEY);
    displayUnit = OFFSET_UNITS.find((u) => u === v) ?? null;
  }
  return displayUnit;
};

const subscribeDisplayUnit = (l: () => void): (() => void) => {
  unitListeners.add(l);
  return () => {
    unitListeners.delete(l);
  };
};

/**
 * The unit the bracket controls (chart order bar, DOM, pad, ticket) show offsets in: the user's
 * last toggle, or null to follow each template's own unit. A view preference for this browser;
 * it never changes a template or what is sent.
 */
export function useBracketDisplayUnit(): OffsetUnit | null {
  return useSyncExternalStore(subscribeDisplayUnit, readDisplayUnit);
}

export function setBracketDisplayUnit(u: OffsetUnit): void {
  displayUnit = u;
  displayUnitRead = true;
  localStore.set(UNIT_KEY, u);
  for (const l of unitListeners) l();
}

// ---- open the editor from anywhere (DOM dropdown, settings, command palette) -----------------

const EVENT = "helm:open-bracket-editor";

export type OpenBracketEditor = { presetId?: string | undefined; symbol?: string | null | undefined };

export function openBracketEditor(detail: OpenBracketEditor = {}): void {
  window.dispatchEvent(new CustomEvent<OpenBracketEditor>(EVENT, { detail }));
}

export function onOpenBracketEditor(cb: (d: OpenBracketEditor) => void): () => void {
  const h = (e: Event): void => {
    cb((e as CustomEvent<OpenBracketEditor>).detail);
  };
  window.addEventListener(EVENT, h);
  return () => {
    window.removeEventListener(EVENT, h);
  };
}
